// src/integrations/formPortability/providerColumnPortabilityUtils.js

function isPlainObject(value) {
  return Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);
}

function normalizeProviderId(providerId) {
  return String(providerId || '').trim().toLowerCase();
}

function getColumnId(column) {
  if (typeof column === 'string') {
    return column;
  }

  return String(column?.id || '');
}

export function isProviderColumnId(columnId, providerId) {
  const provider = normalizeProviderId(providerId);
  const id = String(columnId || '');

  if (!provider || !id) {
    return false;
  }

  return (
    id.startsWith(`providerOutput@${provider}@`) ||
    id.startsWith(`providerMetric@${provider}@`)
  );
}

export function sanitizeProviderColumnOrderForDuplicate(
  columnOrder,
  providerId
) {
  if (!Array.isArray(columnOrder)) {
    return {
      columnOrder,
      removedColumnIds: [],
      changed: false,
    };
  }

  const removedColumnIds = [];
  const keptColumns = [];

  columnOrder.forEach((column) => {
    const columnId = getColumnId(column);

    if (isProviderColumnId(columnId, providerId)) {
      removedColumnIds.push(columnId);
      return;
    }

    keptColumns.push(column);
  });

  if (removedColumnIds.length === 0) {
    return {
      columnOrder,
      removedColumnIds,
      changed: false,
    };
  }

  return {
    columnOrder: keptColumns.map((column, index) => {
      if (typeof column === 'string') {
        return column;
      }

      return {
        ...column,
        order: index,
      };
    }),
    removedColumnIds,
    changed: true,
  };
}

export function sanitizeProviderColumnWidthsForDuplicate(
  columnWidths,
  removedColumnIds = []
) {
  if (!isPlainObject(columnWidths) || removedColumnIds.length === 0) {
    return {
      columnWidths,
      changed: false,
    };
  }

  const removedSet = new Set(removedColumnIds);
  const nextColumnWidths = {};

  Object.entries(columnWidths).forEach(([columnId, width]) => {
    if (removedSet.has(columnId)) {
      return;
    }

    nextColumnWidths[columnId] = width;
  });

  return {
    columnWidths: nextColumnWidths,
    changed: Object.keys(nextColumnWidths).length !==
      Object.keys(columnWidths).length,
  };
}

export function sanitizeTableSurfaceProviderColumnsForDuplicate(
  surface,
  providerId
) {
  if (!isPlainObject(surface)) {
    return {
      surface,
      removedColumnIds: [],
      changed: false,
    };
  }

  const columnResult = sanitizeProviderColumnOrderForDuplicate(
    surface.columnOrder,
    providerId
  );

  if (!columnResult.changed) {
    return {
      surface,
      removedColumnIds: [],
      changed: false,
    };
  }

  const widthResult = sanitizeProviderColumnWidthsForDuplicate(
    surface.columnWidths,
    columnResult.removedColumnIds
  );

  const nextSurface = {
    ...surface,
    columnOrder: columnResult.columnOrder,
  };

  if (widthResult.changed) {
    nextSurface.columnWidths = widthResult.columnWidths;
  }

  return {
    surface: nextSurface,
    removedColumnIds: columnResult.removedColumnIds,
    changed: true,
  };
}

export function sanitizeProviderColumnsInSubmissionSettings(
  submissionSettings,
  providerId
) {
  if (!isPlainObject(submissionSettings)) {
    return {
      settings: submissionSettings,
      diagnostics: {
        touchedSurfaces: [],
        removedColumnIds: [],
      },
    };
  }

  const nextSettings = {
    ...submissionSettings,
  };

  const touchedSurfaces = [];
  const removedColumnIds = [];

  Object.entries(submissionSettings).forEach(([settingsKey, value]) => {
    const result = sanitizeTableSurfaceProviderColumnsForDuplicate(
      value,
      providerId
    );

    if (!result.changed) {
      return;
    }

    nextSettings[settingsKey] = result.surface;
    touchedSurfaces.push(settingsKey);
    removedColumnIds.push(...result.removedColumnIds);
  });

  return {
    settings: nextSettings,
    diagnostics: {
      touchedSurfaces,
      removedColumnIds,
    },
  };
}